import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MIN_PANEL_W = 320;
const MIN_PANEL_H = 240;

function clamp(n: number, min: number, max: number) {
    return Math.min(Math.max(n, min), max);
}

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'half';
    onBack?: () => void;
    showBackButton?: boolean;
    /** When set, the panel can be dragged by the header. */
    draggable?: boolean;
    /** When set, a corner handle resizes the panel. */
    resizable?: boolean;
    /**
     * `glass` (default): translucent + backdrop blur — tint varies with whatever is behind the dialog (tab/layout).
     * `solid`: opaque panel — same look on every screen (use for settings/forms that must look identical everywhere).
     */
    panelSurface?: 'glass' | 'solid';
}

const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    size = 'md',
    onBack,
    showBackButton = false,
    draggable = false,
    resizable = false,
    panelSurface = 'glass'
}) => {
    const isFloating = draggable || resizable;

    const [panel, setPanel] = useState({ x: 0, y: 0, w: MIN_PANEL_W, h: MIN_PANEL_H });

    const dragRef = useRef<{ origX: number; origY: number; startX: number; startY: number } | null>(null);
    const resizeRef = useRef<{ origW: number; origH: number; startX: number; startY: number } | null>(null);

    /** Default: ~lg width, 2/3 viewport height, centered (each open). */
    const resetFloatingPanel = useCallback(() => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const margin = 16;
        const w = clamp(672, MIN_PANEL_W, vw - margin * 2);
        const h = clamp(Math.round((vh * 2) / 3), MIN_PANEL_H, vh - margin * 2);
        const xCentered = Math.round((vw - w) / 2);
        const yCentered = Math.round((vh - h) / 2);
        setPanel({
            w,
            h,
            x: clamp(xCentered, margin, Math.max(margin, vw - w - margin)),
            y: clamp(yCentered, margin, Math.max(margin, vh - h - margin))
        });
    }, []);

    useLayoutEffect(() => {
        if (isOpen && isFloating) {
            resetFloatingPanel();
        }
    }, [isOpen, isFloating, resetFloatingPanel]);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    const endDrag = useCallback(() => {
        dragRef.current = null;
    }, []);

    const endResize = useCallback(() => {
        resizeRef.current = null;
    }, []);

    const onHeaderPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!draggable) return;
        if ((e.target as HTMLElement).closest('button')) return;
        e.preventDefault();
        dragRef.current = {
            origX: panel.x,
            origY: panel.y,
            startX: e.clientX,
            startY: e.clientY
        };
        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const onHeaderPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!draggable || !dragRef.current) return;
        const { origX, origY, startX, startY } = dragRef.current;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        setPanel((p) => ({
            ...p,
            x: clamp(origX + dx, 0, Math.max(0, window.innerWidth - p.w)),
            y: clamp(origY + dy, 0, Math.max(0, window.innerHeight - p.h))
        }));
    };

    const onHeaderPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        if (dragRef.current) {
            try {
                e.currentTarget.releasePointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
            endDrag();
        }
    };

    const onResizePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!resizable) return;
        e.preventDefault();
        e.stopPropagation();
        resizeRef.current = {
            origW: panel.w,
            origH: panel.h,
            startX: e.clientX,
            startY: e.clientY
        };
        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const onResizePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!resizable || !resizeRef.current) return;
        const { origW, origH, startX, startY } = resizeRef.current;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        setPanel((p) => ({
            ...p,
            w: clamp(origW + dx, MIN_PANEL_W, Math.max(MIN_PANEL_W, window.innerWidth - p.x)),
            h: clamp(origH + dy, MIN_PANEL_H, Math.max(MIN_PANEL_H, window.innerHeight - p.y))
        }));
    };

    const onResizePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        if (resizeRef.current) {
            try {
                e.currentTarget.releasePointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
            endResize();
        }
    };

    if (!isOpen) return null;

    const sizeClasses = {
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
        half: 'w-1/2 h-[65vh] max-w-none max-h-none'
    };

    const panelShellClass =
        panelSurface === 'solid'
            ? `relative rounded-2xl border border-slate-600/70 bg-[#0d1117] shadow-2xl ring-1 ring-white/10 ${
                  size === 'half' ? 'flex flex-col' : ''
              }`
            : `relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl ${
                  size === 'half' ? 'flex flex-col' : ''
              }`;

    const headerDividerClass = panelSurface === 'solid' ? 'border-b border-slate-700/80' : 'border-b border-white/10';

    const headerBlock = title && (
        <div
            className={`flex items-center justify-between p-6 ${headerDividerClass} shrink-0 ${
                draggable ? 'cursor-grab active:cursor-grabbing select-none touch-none' : ''
            }`}
            onPointerDown={onHeaderPointerDown}
            onPointerMove={onHeaderPointerMove}
            onPointerUp={onHeaderPointerUp}
            onPointerCancel={onHeaderPointerUp}
        >
            <h2 className="text-xl font-semibold text-white pr-2">{title}</h2>
            <div className="flex items-center space-x-2 shrink-0">
                {onBack && (
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (showBackButton) {
                                onBack();
                            }
                        }}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${
                            showBackButton
                                ? 'text-gray-400 hover:text-white hover:bg-white/10 opacity-100 pointer-events-auto'
                                : 'opacity-0 pointer-events-none'
                        }`}
                        aria-label="Go back"
                        disabled={!showBackButton}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span className="text-base font-medium">Back</span>
                    </button>
                )}
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/10"
                    aria-label="Close modal"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
    );

    const contentBlock = (
        <div
            className={`p-6 ${size === 'half' || isFloating ? 'flex-1 min-h-0 overflow-hidden flex flex-col' : ''}`}
        >
            {children}
        </div>
    );

    const floatingMarkup = (
        <div className="fixed inset-0 z-[10000] overflow-hidden">
            <div
                className="fixed inset-0 z-0 bg-black/50 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />
            <div
                className={`fixed z-[10001] flex flex-col overflow-hidden ${panelShellClass}`}
                style={{
                    left: panel.x,
                    top: panel.y,
                    width: panel.w,
                    height: panel.h
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {headerBlock}
                {contentBlock}
                {resizable && (
                    <div
                        className="absolute right-1 bottom-1 w-4 h-4 cursor-nwse-resize text-white/40 hover:text-white/70"
                        aria-hidden
                        onPointerDown={onResizePointerDown}
                        onPointerMove={onResizePointerMove}
                        onPointerUp={onResizePointerUp}
                        onPointerCancel={onResizePointerUp}
                        title="Resize"
                    >
                        <svg viewBox="0 0 16 16" className="w-full h-full" fill="currentColor">
                            <path d="M16 16h-4v-2h2v-2h2v4zM16 10h-2V8h2v2zM10 16H8v-2h2v2z" />
                        </svg>
                    </div>
                )}
            </div>
        </div>
    );

    const centeredMarkup = (
        <div className="fixed inset-0 z-[10000] overflow-y-auto">
            <div
                className="fixed inset-0 z-0 bg-black/50 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />
            <div
                className={`relative z-[10001] flex ${
                    size === 'half' ? 'min-h-full items-center justify-center' : 'min-h-full items-center justify-center'
                } p-4`}
            >
                <div
                    className={`${size === 'half' ? sizeClasses[size] : `w-full ${sizeClasses[size]}`} ${panelShellClass} transform transition-all duration-300`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {headerBlock}
                    {contentBlock}
                </div>
            </div>
        </div>
    );

    return createPortal(isFloating ? floatingMarkup : centeredMarkup, document.body);
};

export default Modal;
